import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import campagnesMediasApi from '../services/campagnes-medias';
import concessionsApi from '../services/concessions';
import './ReportingAPV.css';
import './CampagnesMediasAdmin.css';

// Page Campagnes médias — nouveau modèle (2026-06).
// Les campagnes ne sont plus saisies manuellement : elles sont dérivées du champ
// « Sujet » des leads VN/VO (actifs + archives). Deux onglets :
//   • 📊 Tableau de bord   — total de leads par campagne normalisée + plage de dates
//   • 🏷️ Référentiel sujets — normalise les sujets bruts en libellés de campagne

const TABS = [
  { id: 'dashboard',   label: '📊 Tableau de bord' },
  { id: 'referentiel', label: '🏷️ Référentiel des sujets' },
];

const PRESETS = [
  { id: 'mois-en-cours', label: 'Mois en cours' },
  { id: 'mois-dernier',  label: 'Mois dernier' },
  { id: 'trimestre',     label: 'Trimestre' },
  { id: 'annee',         label: 'Année' },
  { id: 'tout',          label: 'Tout' },
];

const BAR_COLORS = ['#0891b2', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#ef4444'];

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'mois-en-cours':
      return { debut: toISO(new Date(today.getFullYear(), today.getMonth(), 1)), fin: toISO(today) };
    case 'mois-dernier':
      return {
        debut: toISO(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        fin:   toISO(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'trimestre': {
      const q = Math.floor(today.getMonth() / 3);
      return { debut: toISO(new Date(today.getFullYear(), q * 3, 1)), fin: toISO(today) };
    }
    case 'annee':
      return { debut: toISO(new Date(today.getFullYear(), 0, 1)), fin: toISO(today) };
    case 'tout':
    default:
      return { debut: '', fin: '' };
  }
}

const fmtInt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function downloadCsv(filename, headers, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(';'), ...rows.map(r => r.map(escape).join(';'))];
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CampagnesMediasAdmin({ setHeaderAction }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [concessions, setConcessions] = useState([]);
  const [filterConcession, setFilterConcession] = useState('');

  // Tableau de bord
  const [preset, setPreset] = useState('annee');
  const initial = computeRange('annee');
  const [debut, setDebut] = useState(initial.debut);
  const [fin, setFin] = useState(initial.fin);
  const [dashRows, setDashRows] = useState([]);
  const [dashTotals, setDashTotals] = useState({ nb_vn: 0, nb_vo: 0, total: 0, nb_campagnes: 0 });
  const [dashSearch, setDashSearch] = useState('');
  const [loadingDash, setLoadingDash] = useState(false);

  // Référentiel
  const [sujets, setSujets] = useState([]);
  const [libelles, setLibelles] = useState([]);
  const [labelEdits, setLabelEdits] = useState({}); // sujet_brut -> libellé courant
  const originalLabels = useRef({});
  const [refSearch, setRefSearch] = useState('');
  const [loadingRef, setLoadingRef] = useState(false);
  const [savingRef, setSavingRef] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    concessionsApi.getAll().then(r => setConcessions(r.data?.concessions || [])).catch(() => {});
  }, []);

  const applyPreset = (id) => {
    setPreset(id);
    const r = computeRange(id);
    setDebut(r.debut); setFin(r.fin);
  };

  // ── Chargement tableau de bord ──
  const loadDashboard = useCallback(async () => {
    setLoadingDash(true);
    try {
      const r = await campagnesMediasApi.getDashboard({
        debut: debut || undefined,
        fin: fin || undefined,
        concession_id: filterConcession || undefined,
      });
      setDashRows(r.data?.campagnes || []);
      setDashTotals(r.data?.totals || { nb_vn: 0, nb_vo: 0, total: 0, nb_campagnes: 0 });
    } catch (e) {
      console.error('Erreur chargement tableau de bord campagnes:', e);
      showToast('Erreur de chargement du tableau de bord', 'error');
    } finally {
      setLoadingDash(false);
    }
  }, [debut, fin, filterConcession]);

  // ── Chargement référentiel ──
  const loadReferentiel = useCallback(async () => {
    setLoadingRef(true);
    try {
      const r = await campagnesMediasApi.getSujets({ concession_id: filterConcession || undefined });
      const rows = r.data?.sujets || [];
      setSujets(rows);
      setLibelles(r.data?.libelles_distincts || []);
      const init = {};
      rows.forEach(s => { init[s.sujet_brut] = s.libelle || ''; });
      setLabelEdits(init);
      originalLabels.current = { ...init };
    } catch (e) {
      console.error('Erreur chargement référentiel sujets:', e);
      showToast('Erreur de chargement du référentiel', 'error');
    } finally {
      setLoadingRef(false);
    }
  }, [filterConcession]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
    else loadReferentiel();
  }, [activeTab, loadDashboard, loadReferentiel]);

  // ── Sauvegarde référentiel ──
  const dirtyItems = useMemo(() => sujets
    .filter(s => (labelEdits[s.sujet_brut] || '') !== (originalLabels.current[s.sujet_brut] || ''))
    .map(s => ({ sujet_brut: s.sujet_brut, libelle: labelEdits[s.sujet_brut] || '' })),
  [sujets, labelEdits]);

  const saveReferentiel = useCallback(async () => {
    if (dirtyItems.length === 0) return;
    setSavingRef(true);
    try {
      await campagnesMediasApi.saveSujets(dirtyItems);
      showToast(`${dirtyItems.length} sujet(s) normalisé(s)`);
      await loadReferentiel();
    } catch (e) {
      showToast(e.response?.data?.error || 'Erreur d\'enregistrement', 'error');
    } finally {
      setSavingRef(false);
    }
  }, [dirtyItems, loadReferentiel]);

  // ── Export CSV tableau de bord ──
  const exportDashboard = useCallback(() => {
    const c = concessions.find(x => String(x.id) === String(filterConcession));
    const scope = c ? c.nom : 'toutes-concessions';
    downloadCsv(
      `campagnes_${scope}_${debut || 'debut'}_${fin || 'fin'}.csv`,
      ['Campagne', 'Leads VN', 'Leads VO', 'Total'],
      dashFiltered.map(r => [r.campagne, r.nb_vn, r.nb_vo, r.total]),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashRows, dashSearch, concessions, filterConcession, debut, fin]);

  // ── Données dérivées ──
  const dashFiltered = useMemo(() => {
    const q = dashSearch.trim().toLowerCase();
    return q ? dashRows.filter(r => (r.campagne || '').toLowerCase().includes(q)) : dashRows;
  }, [dashRows, dashSearch]);

  const chartData = useMemo(
    () => dashFiltered.slice(0, 12).map(r => ({ name: r.campagne, total: r.total })),
    [dashFiltered]
  );

  const sujetsFiltered = useMemo(() => {
    const q = refSearch.trim().toLowerCase();
    if (!q) return sujets;
    return sujets.filter(s =>
      (s.sujet_brut || '').toLowerCase().includes(q) ||
      (labelEdits[s.sujet_brut] || '').toLowerCase().includes(q));
  }, [sujets, refSearch, labelEdits]);

  const nbNormalises = useMemo(
    () => sujets.filter(s => (labelEdits[s.sujet_brut] || '').trim()).length,
    [sujets, labelEdits]
  );

  // ── Action header selon onglet ──
  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'dashboard') {
      setHeaderAction({ secondary: [{ label: 'Export CSV', onClick: exportDashboard }] });
    } else {
      setHeaderAction({
        primary: {
          label: dirtyItems.length ? `Enregistrer (${dirtyItems.length})` : 'Enregistré',
          onClick: saveReferentiel,
        },
      });
    }
    return () => setHeaderAction(null);
  }, [activeTab, setHeaderAction, exportDashboard, saveReferentiel, dirtyItems.length]);

  return (
    <div className="page-container">
      <div className="page-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            className={activeTab === t.id ? 'active' : ''}
            onClick={() => setActiveTab(t.id)}
          >{t.label}</button>
        ))}
      </div>

      {/* ───────────────────────── TABLEAU DE BORD ───────────────────────── */}
      {activeTab === 'dashboard' && (
        <>
          <div className="page-toolbar" style={{ flexWrap: 'wrap' }}>
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher une campagne..."
              value={dashSearch}
              onChange={(e) => setDashSearch(e.target.value)}
            />
            <select
              className={`page-select ${filterConcession ? 'active-filter' : ''}`}
              value={filterConcession}
              onChange={(e) => setFilterConcession(e.target.value)}
              title="Filtrer par concession"
            >
              <option value="">Toutes les concessions</option>
              {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <div className="reporting-segment cm-presets" role="group" aria-label="Période">
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className={preset === p.id ? 'active' : ''}
                  onClick={() => applyPreset(p.id)}
                >{p.label}</button>
              ))}
            </div>
            <div className="cm-dates">
              <label>Du <input type="date" value={debut} onChange={(e) => { setDebut(e.target.value); setPreset(''); }} /></label>
              <label>au <input type="date" value={fin} onChange={(e) => { setFin(e.target.value); setPreset(''); }} /></label>
            </div>
          </div>

          <div className="reporting-kpi-grid">
            <div className="reporting-kpi-card">
              <div className="label">Total leads</div>
              <div className="value">{fmtInt(dashTotals.total)}</div>
              <div className="sub">sur la période</div>
            </div>
            <div className="reporting-kpi-card">
              <div className="label">Campagnes</div>
              <div className="value">{fmtInt(dashTotals.nb_campagnes)}</div>
              <div className="sub">sujets normalisés distincts</div>
            </div>
            <div className="reporting-kpi-card">
              <div className="label">Leads VN</div>
              <div className="value">{fmtInt(dashTotals.nb_vn)}</div>
              <div className="sub">{dashTotals.total > 0 ? `${Math.round((dashTotals.nb_vn / dashTotals.total) * 100)} %` : '—'}</div>
            </div>
            <div className="reporting-kpi-card">
              <div className="label">Leads VO</div>
              <div className="value">{fmtInt(dashTotals.nb_vo)}</div>
              <div className="sub">{dashTotals.total > 0 ? `${Math.round((dashTotals.nb_vo / dashTotals.total) * 100)} %` : '—'}</div>
            </div>
          </div>

          <div className="reporting-chart-card">
            <h3>📊 Top campagnes (volume de leads)</h3>
            {loadingDash ? (
              <div className="reporting-empty">Chargement…</div>
            ) : chartData.length === 0 ? (
              <div className="reporting-empty">Aucun lead VN/VO sur cette période.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 34)}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} allowDecimals={false} />
                  <YAxis
                    type="category" dataKey="name" width={220}
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    tickFormatter={(v) => (v.length > 34 ? v.slice(0, 32) + '…' : v)}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)' }}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div className="reporting-tooltip">
                          <div className="tooltip-title">{payload[0].payload.name}</div>
                          <div className="tooltip-row"><span>Leads</span><span>{fmtInt(payload[0].value)}</span></div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, i) => <Cell key={entry.name} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="reporting-chart-card">
            <h3>🏷️ Détail par campagne</h3>
            {loadingDash ? (
              <div className="reporting-empty">Chargement…</div>
            ) : dashFiltered.length === 0 ? (
              <div className="reporting-empty">Aucune campagne sur cette période.</div>
            ) : (
              <div className="table-container">
                <table className="cm-table">
                  <thead>
                    <tr>
                      <th>Campagne</th>
                      <th className="num">Leads VN</th>
                      <th className="num">Leads VO</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashFiltered.map(r => (
                      <tr key={r.campagne}>
                        <td>{r.campagne}</td>
                        <td className="num">{fmtInt(r.nb_vn)}</td>
                        <td className="num">{fmtInt(r.nb_vo)}</td>
                        <td className="num"><strong>{fmtInt(r.total)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total ({fmtInt(dashFiltered.length)} campagnes)</td>
                      <td className="num">{fmtInt(dashFiltered.reduce((s, r) => s + r.nb_vn, 0))}</td>
                      <td className="num">{fmtInt(dashFiltered.reduce((s, r) => s + r.nb_vo, 0))}</td>
                      <td className="num"><strong>{fmtInt(dashFiltered.reduce((s, r) => s + r.total, 0))}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ───────────────────────── RÉFÉRENTIEL ───────────────────────── */}
      {activeTab === 'referentiel' && (
        <>
          <div className="page-toolbar" style={{ flexWrap: 'wrap' }}>
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher un sujet..."
              value={refSearch}
              onChange={(e) => setRefSearch(e.target.value)}
            />
            <select
              className={`page-select ${filterConcession ? 'active-filter' : ''}`}
              value={filterConcession}
              onChange={(e) => setFilterConcession(e.target.value)}
              title="Filtrer par concession"
            >
              <option value="">Toutes les concessions</option>
              {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <div className="cm-ref-counter">
              {fmtInt(nbNormalises)} / {fmtInt(sujets.length)} sujets normalisés
            </div>
          </div>

          <div className="cm-ref-note">
            💡 Chaque <strong>sujet</strong> de lead VN/VO (actifs + archives) apparaît ci-dessous.
            Saisissez un <strong>nom de campagne</strong> pour regrouper plusieurs sujets sous une même
            campagne (ex. « #vn OFFRE STAR - Avril 2026 » et « OFFRE STAR - Avril 2026 » → <em>OFFRE STAR Avril 2026</em>).
            Un sujet laissé vide reste compté sous son intitulé d'origine. Les modifications sont prises en
            compte après <strong>Enregistrer</strong>.
          </div>

          <div className="table-container">
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Sujet du lead (brut)</th>
                  <th className="num">VN</th>
                  <th className="num">VO</th>
                  <th className="num">Total</th>
                  <th style={{ width: '38%' }}>Nom de campagne (normalisé)</th>
                </tr>
              </thead>
              <tbody>
                {loadingRef && (
                  <tr><td colSpan={5} className="cm-empty">Chargement…</td></tr>
                )}
                {!loadingRef && sujetsFiltered.length === 0 && (
                  <tr><td colSpan={5} className="cm-empty">Aucun sujet de lead VN/VO.</td></tr>
                )}
                {!loadingRef && sujetsFiltered.map(s => {
                  const val = labelEdits[s.sujet_brut] || '';
                  const dirty = val !== (originalLabels.current[s.sujet_brut] || '');
                  return (
                    <tr key={s.sujet_brut} className={dirty ? 'cm-row-dirty' : ''}>
                      <td className="cm-sujet">{s.sujet_brut}</td>
                      <td className="num">{fmtInt(s.nb_vn)}</td>
                      <td className="num">{fmtInt(s.nb_vo)}</td>
                      <td className="num"><strong>{fmtInt(s.nb_leads)}</strong></td>
                      <td>
                        <input
                          type="text"
                          className="cm-label-input"
                          list="cm-libelles"
                          placeholder={s.sujet_brut}
                          value={val}
                          onChange={(e) => setLabelEdits(prev => ({ ...prev, [s.sujet_brut]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <datalist id="cm-libelles">
              {libelles.map(l => <option key={l} value={l} />)}
            </datalist>
          </div>

          {savingRef && <div className="cm-empty">Enregistrement…</div>}
        </>
      )}

      {toast && (
        <div className={`cm-toast ${toast.kind === 'error' ? 'cm-toast-error' : 'cm-toast-success'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

export default CampagnesMediasAdmin;
